"use client"

import { Separator } from "@/components/ui/separator"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useState, useEffect } from "react"
import { useRouter, useSearchParams } from 'next/navigation'

interface Category {
  id: string
  name: string
  slug: string
}

interface Brand {
  id: string
  name: string
  slug: string
}

interface ProductFiltersProps {
  categories?: Category[]
  brands?: Brand[]
  searchParams?: any
  routeBase?: string
  hideBrandFilter?: boolean
}

export function ProductFilters({
  categories = [],
  brands = [],
  searchParams = {},
  routeBase = "/shop",
  hideBrandFilter = false,
}: ProductFiltersProps) {
  const router = useRouter();
  const urlSearchParams = useSearchParams();
  
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedBrands, setSelectedBrands] = useState<string[]>([])
  const [selectedSizes, setSelectedSizes] = useState<string[]>([])
  const [selectedColors, setSelectedColors] = useState<string[]>([])

  const updateParams = (newParams: Record<string, string>) => {
    const params = new URLSearchParams(urlSearchParams);
    
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    
    const query = params.toString();
    router.push(query ? `${routeBase}?${query}` : routeBase);
  };

  // Initialize filters from URL params
  useEffect(() => {
    const categoryParam = searchParams.category || urlSearchParams.get("category") || ""
    const brandParam = searchParams.brand || urlSearchParams.get("brand") || ""
    const minPriceParam = searchParams.minPrice || urlSearchParams.get("minPrice") || ""
    const maxPriceParam = searchParams.maxPrice || urlSearchParams.get("maxPrice") || ""

    if (categoryParam) {
      setSelectedCategories([categoryParam])
    } else {
      setSelectedCategories([])
    }
    if (brandParam) {
      setSelectedBrands([brandParam])
    } else {
      setSelectedBrands([])
    }
    if (minPriceParam) {
      setMinPrice(minPriceParam)
    } else {
      setMinPrice("")
    }
    if (maxPriceParam) {
      setMaxPrice(maxPriceParam)
    } else {
      setMaxPrice("")
    }
  }, [searchParams, urlSearchParams])

  const handleCategoryChange = (categorySlug: string, checked: boolean) => {
    if (checked) {
      setSelectedCategories([categorySlug])
      updateParams({ category: categorySlug, page: '1' })
    } else {
      setSelectedCategories([])
      updateParams({ category: '', page: '1' })
    }
  }

  const handleBrandChange = (brandSlug: string, checked: boolean) => {
    if (checked) {
      setSelectedBrands([brandSlug])
      updateParams({ brand: brandSlug, page: '1' })
    } else {
      setSelectedBrands([])
      updateParams({ brand: '', page: '1' })
    }
  }

  const handlePriceApply = () => {
    updateParams({
      minPrice: minPrice,
      maxPrice: maxPrice,
      page: '1'
    })
  }

  const handleSizeChange = (size: string) => {
    if (selectedSizes.includes(size)) {
      setSelectedSizes(selectedSizes.filter(s => s !== size))
    } else {
      setSelectedSizes([...selectedSizes, size])
    }
  }

  const handleColorChange = (color: string) => {
    if (selectedColors.includes(color)) {
      setSelectedColors(selectedColors.filter(c => c !== color))
    } else {
      setSelectedColors([...selectedColors, color])
    }
  }



  const applyFilters = () => {
    const params: Record<string, string> = { page: '1' }
    
    if (selectedBrands.length > 0) {
      params.brand = selectedBrands.join(',')
    }
    if (selectedSizes.length > 0) {
      params.size = selectedSizes.join(',')
    }
    if (selectedColors.length > 0) {
      params.color = selectedColors.join(',')
    }
    
    updateParams(params)
  }

  const clearAllFilters = () => {
    updateParams({
      category: '',
      minPrice: '',
      maxPrice: '',
      brand: '',
      size: '',
      color: '',
      page: '1'
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Filters</h3>
        <Button variant="ghost" size="sm" onClick={clearAllFilters}>
          Clear all
        </Button>
      </div>

      {/* Categories */}
      <div>
        <h4 className="font-medium mb-3">Categories</h4>
        <div className="space-y-2">
          {Array.isArray(categories) && categories.map((category) => (
            <div key={category.id} className="flex items-center space-x-2">
              <Checkbox 
                id={category.slug}
                checked={selectedCategories.includes(category.slug)}
                onCheckedChange={(checked) => handleCategoryChange(category.slug, checked as boolean)}
              />
              <Label htmlFor={category.slug} className="text-sm cursor-pointer">
                {category.name}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Price Range */}
      <div>
        <h4 className="font-medium mb-3">Price Range</h4>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="w-20"
              min={0}
              suppressHydrationWarning
            />
            <span className="text-sm text-muted-foreground">to</span>
            <Input
              type="number"
              placeholder="Max"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="w-20"
              min={0}
              suppressHydrationWarning
            />
          </div>
          <Button size="sm" onClick={handlePriceApply} className="w-full">
            Apply Price Filter
          </Button>
        </div>
      </div>

      <Separator />

      {!hideBrandFilter && (
        <>
          {/* Brand */}
          <div>
            <h4 className="font-medium mb-3">Brand</h4>
            <div className="space-y-2">
              {Array.isArray(brands) && brands.map((brand) => (
                <div key={brand.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={brand.slug}
                    checked={selectedBrands.includes(brand.slug)}
                    onCheckedChange={(checked) => handleBrandChange(brand.slug, checked as boolean)}
                  />
                  <Label htmlFor={brand.slug} className="text-sm cursor-pointer">
                    {brand.name}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />
        </>
      )}

      {/* Size */}
      {/* <div>
        <h4 className="font-medium mb-3">Size</h4>
        <div className="grid grid-cols-3 gap-2">
          {["XS", "S", "M", "L", "XL", "XXL"].map((size) => (
            <Button 
              key={size} 
              variant={selectedSizes.includes(size) ? "default" : "outline"} 
              size="sm" 
              className="h-8"
              onClick={() => handleSizeChange(size)}
            >
              {size}
            </Button>
          ))}
        </div>
      </div> */}

      {/* Color */}
      {/* <div>
        <h4 className="font-medium mb-3">Color</h4>
        <div className="grid grid-cols-6 gap-2">
          {[
            { name: "Black", class: "bg-black" },
            { name: "White", class: "bg-white" },
            { name: "Red", class: "bg-red-500" },
            { name: "Blue", class: "bg-blue-500" },
            { name: "Green", class: "bg-green-500" },
            { name: "Yellow", class: "bg-yellow-500" },
            { name: "Purple", class: "bg-purple-500" },
            { name: "Pink", class: "bg-pink-500" },
            { name: "Gray", class: "bg-gray-500" },
            { name: "Orange", class: "bg-orange-500" },
            { name: "Indigo", class: "bg-indigo-500" },
            { name: "Teal", class: "bg-teal-500" },
          ].map((color) => (
            <button
              key={color.name}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                selectedColors.includes(color.name) 
                  ? "border-primary scale-110" 
                  : "border-gray-300 hover:border-gray-400"
              } ${color.class} ${
                color.class === "bg-white" ? "border-gray-400" : ""
              }`}
              onClick={() => handleColorChange(color.name)}
              title={color.name}
            />
          ))}
        </div>
      </div> */}



      {!hideBrandFilter && (
        <Button className="w-full" onClick={applyFilters}>
          Apply Filters
        </Button>
      )}
    </div>
  )
}
